import { toRows } from '../toRows';
import { createSlice } from '@reduxjs/toolkit';
import { vehicleApi } from '@store/rtk/vehicle';
import { TableData } from '@uikit/Tables/types';
import { sliderApi } from '@store/rtk/slider';
import { IMAGE_STORAGE_URL } from '@src/utils/constants';
import { IMAGES } from '@image/images';
import { selectItemType } from '@src/types';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

export type vehicleItemType = {
  color: selectItemType;
  effectiveDate: string;
  expirationDate: string;
  id: number;
  images: null;
  inspectionOrganization: null;
  issuedNumber: string;
  liftLocation: null;
  make: selectItemType;
  model: selectItemType;
  plate: string;
  status: selectItemType;
  type: selectItemType;
  vin: string;
  year: number;
};

export type VehicleSliderDataType = {
  id: number;
  title: string;
  img: string;
  date: string;
};

type VehicleDataType = {
  vehicleData: {
    content: Array<vehicleItemType>;
    page: number;
    size: number;
  };
  vehicleSliderData: Array<VehicleSliderDataType>;
  vehicleRegistrationTableData: TableData;
  vehicleInspectionTableData: TableData;
  vehiclePermitTableData: TableData;
  vehicleServiceDatesTableData: TableData;
};

const {
  vehicleRegistrationTableColumns,
  vehicleInspectionTableColumns,
  vehiclePermitTableColumns,
  vehicleServiceDatesTableColumns,
} = TABLE_COLUMNS;

const initialState: VehicleDataType = {
  vehicleData: {
    content: [],
    page: 0,
    size: 10,
  },
  vehicleSliderData: [
    {
      id: 1,
      title: 'FRONT',
      img: IMAGES.defaultImage,
      date: '',
    },
    {
      id: 2,
      title: 'REAR',
      img: IMAGES.defaultImage,
      date: '',
    },
    {
      id: 3,
      title: 'PASSENGER SIDE',
      img: IMAGES.defaultImage,
      date: '',
    },
    {
      id: 4,
      title: 'DRIVER SIDE',
      img: IMAGES.defaultImage,
      date: '',
    },
  ],
  vehicleRegistrationTableData: {
    columns: vehicleRegistrationTableColumns,
    rows: [],
  },
  vehicleInspectionTableData: {
    columns: vehicleInspectionTableColumns,
    rows: [],
  },
  vehiclePermitTableData: {
    columns: vehiclePermitTableColumns,
    rows: [],
  },
  vehicleServiceDatesTableData: {
    columns: vehicleServiceDatesTableColumns,
    rows: [],
  },
};

export const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        vehicleApi.endpoints.getVehicleData.matchFulfilled,
        (state, { payload }) => {
          state.vehicleData.content = payload;
        }
      )
      .addMatcher(
        sliderApi.endpoints.getVehicleSliderImages.matchFulfilled,
        (state, { payload }) => {
          const version = Date.now();
          state.vehicleSliderData = payload
            ? [
                {
                  id: 1,
                  title: 'FRONT',
                  img: payload.front
                    ? `${IMAGE_STORAGE_URL}/${payload.front}?${version}`
                    : IMAGES.defaultImage,
                  date: payload.date ?? '',
                },
                {
                  id: 2,
                  title: 'REAR',
                  img: payload.rear
                    ? `${IMAGE_STORAGE_URL}/${payload.rear}?${version}`
                    : IMAGES.defaultImage,
                  date: payload.date ?? '',
                },
                {
                  id: 3,
                  title: 'PASSENGER SIDE',
                  img: payload.sidePassenger
                    ? `${IMAGE_STORAGE_URL}/${payload.sidePassenger}?${version}`
                    : IMAGES.defaultImage,
                  date: payload.date ?? '',
                },
                {
                  id: 4,
                  title: 'DRIVER SIDE',
                  img: payload.sideDriver
                    ? `${IMAGE_STORAGE_URL}/${payload.sideDriver}?${version}`
                    : IMAGES.defaultImage,
                  date: payload.date ?? '',
                },
              ]
            : [
                {
                  id: 1,
                  title: 'FRONT',
                  img: IMAGES.defaultImage,
                  date: '',
                },
                {
                  id: 2,
                  title: 'REAR',
                  img: IMAGES.defaultImage,
                  date: '',
                },
                {
                  id: 3,
                  title: 'PASSENGER SIDE',
                  img: IMAGES.defaultImage,
                  date: '',
                },
                {
                  id: 4,
                  title: 'DRIVER SIDE',
                  img: IMAGES.defaultImage,
                  date: '',
                },
              ];
        }
      )
      .addMatcher(
        vehicleApi.endpoints.getVehicleRegistrationTableData.matchFulfilled,
        (state, { payload }) => {
          state.vehicleRegistrationTableData = {
            ...state.vehicleRegistrationTableData,
            rows: toRows(payload),
          };
        }
      )
      .addMatcher(
        vehicleApi.endpoints.getVehicleInspectionTableData.matchFulfilled,
        (state, { payload }) => {
          state.vehicleInspectionTableData = {
            ...state.vehicleInspectionTableData,
            rows: toRows(payload),
          };
        }
      )
      .addMatcher(
        vehicleApi.endpoints.getVehiclePermitTableData.matchFulfilled,
        (state, { payload }) => {
          state.vehiclePermitTableData = {
            ...state.vehiclePermitTableData,
            rows: toRows(payload),
          };
        }
      )
      .addMatcher(
        vehicleApi.endpoints.getVehicleServiceDatesTableData.matchFulfilled,
        (state, { payload }) => {
          state.vehicleServiceDatesTableData = {
            ...state.vehicleServiceDatesTableData,
            rows: toRows(payload),
          };
        }
      );
  },
});

export const selectVehicle = (state: { vehicle: VehicleDataType }) =>
  state.vehicle;
export const vehicleReducer = vehicleSlice.reducer;
